import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import ChefImage from '../assets/images/Chef.png';
import ChaseWaveImage from '../assets/images/ChaseWave.png';
import ChaseThumbsUpImage from '../assets/images/ChaseThumbsUp.png';
import '../styles/gameboy.css';

const SELECT_SOUND = process.env.PUBLIC_URL + '/music/Select.mp3';

// Helper to parse *text* as red highlighted text
const parseHighlightedText = (text) => {
  if (!text || typeof text !== 'string') return text;
  const parts = text.split(/(\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('*') && part.endsWith('*')) {
      return <span key={i} style={{ color: '#ef4444', fontWeight: '600' }}>{part.slice(1, -1)}</span>;
    }
    return part;
  });
};

/**
 * TutorialBackdrop - Persistent dark overlay that stays mounted during tutorial sequences
 * Use this wrapper around multiple TutorialOverlay components to prevent backdrop flashing
 */
export const TutorialBackdrop = ({ visible, children }) => {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            zIndex: 9998,
          }}
        />
      )}
    </AnimatePresence>
  );
};

/**
 * TutorialOverlay - GameBoy-style dialog overlay for tutorials
 *
 * Shows Chef character with typed text and Yes/No buttons (tappable, no D-pad)
 */
const TutorialOverlay = ({
  visible,
  message = ["Alright! Now that we are in", "the app, would you like a", "tutorial or figure it out", "on your own?"],
  onYes,
  onNo,
  onDismiss,
  characterImage = ChefImage,
  hideButtons = false,
  spotlightMode = false, // 'profile' or 'info-icon'
  yesText = "Yes, show me!",
  noText = "No thanks",
  // New props for dual character mode
  mode = 'normal', // 'normal', 'intro-chase', 'chase-hello', 'chase-congrats'
  onNext, // For intro-chase mode
  onOption1, // Add Card/Binder
  onOption2, // Post a Card
  onOption3, // Set up a Trade
  option1Text = "Add a Card or Binder",
  option2Text = "Post a Card",
  option3Text = "Set up a Trade",
  // Congrats mode props
  onContinue, // For chase-congrats mode
  continueText = "Continue",
  onSecondary, // For chase-congrats mode secondary button
  secondaryText = "More Tutorials",
  // Close button prop
  onClose, // Optional close handler for X button
}) => {
  const { theme, isDark } = useTheme();
  // Typewriter state
  const [displayedText, setDisplayedText] = useState('');
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const [buttonsVisible, setButtonsVisible] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  // Content fade state for smooth transitions
  const [contentVisible, setContentVisible] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Track window size for spotlight positioning
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Full text as single string
  const fullText = message.join('\n');

  // Play click sound (respects sound setting)
  const playClickSound = useCallback(() => {
    // Check if sound is enabled
    if (localStorage.getItem('soundEnabled') === 'false') return;
    try {
      const clickAudio = new Audio(SELECT_SOUND);
      clickAudio.volume = 0.3;
      clickAudio.play().catch(() => {});
    } catch (err) {}
  }, []);

  // Track previous message for transition detection
  const [prevMessage, setPrevMessage] = useState('');

  // Handle content fade in/out for smooth transitions
  useEffect(() => {
    if (visible && !isTransitioning) {
      // Delay before fading content in - gives time for overlay to appear first
      const timer = setTimeout(() => {
        setContentVisible(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [visible, isTransitioning]);

  // Reset state when overlay becomes visible OR when message changes
  // Using fullText as dependency so it resets for new messages
  useEffect(() => {
    if (visible) {
      setDisplayedText('');
      setIsTypingComplete(false);
      setButtonsVisible(false);

      // If message changed while already visible, do a fade out then fade in transition
      if (prevMessage !== fullText && prevMessage !== '') {
        setIsTransitioning(true);
        setContentVisible(false);
        const timer = setTimeout(() => {
          setIsTransitioning(false);
          // contentVisible will be set to true by the other useEffect after transition ends
        }, 400); // Longer delay for smoother transition between steps
        setPrevMessage(fullText);
        return () => clearTimeout(timer);
      }
      setPrevMessage(fullText);
    } else {
      setContentVisible(false);
      setPrevMessage('');
    }
  }, [visible, fullText, prevMessage]);

  // Typewriter effect - character by character
  useEffect(() => {
    if (!visible || isTypingComplete) return;

    if (displayedText.length < fullText.length) {
      const timer = setTimeout(() => {
        setDisplayedText(fullText.slice(0, displayedText.length + 1));
      }, 25);
      return () => clearTimeout(timer);
    } else {
      setIsTypingComplete(true);
      setTimeout(() => setButtonsVisible(true), 1000);
    }
  }, [visible, displayedText, fullText, isTypingComplete]);

  // Skip typing animation on tap, or dismiss if hideButtons mode
  const handleTextBoxTap = () => {
    if (!isTypingComplete) {
      setDisplayedText(fullText);
      setIsTypingComplete(true);
      setTimeout(() => setButtonsVisible(true), 100);
    } else if (hideButtons && onDismiss) {
      // If no buttons and typing done, tap to dismiss
      playClickSound();
      onDismiss();
    }
  };

  const handleYes = () => {
    playClickSound();
    onYes?.();
  };

  const handleNo = () => {
    playClickSound();
    onNo?.();
  };

  if (!visible) return null;

  const styleSheet = styles(theme, isDark);

  // Spotlight mode - overlay with hole for specific element
  if (spotlightMode) {
    const isProfileSpotlight = spotlightMode === 'profile';
    const isInfoIconSpotlight = spotlightMode === 'info-icon';
    const isBottomNavCardsSpotlight = spotlightMode === 'bottom-nav-cards';
    const isBottomNavAddSpotlight = spotlightMode === 'bottom-nav-add';
    const isBottomNavChatSpotlight = spotlightMode === 'bottom-nav-chat';

    // Get actual safe area from CSS env() - this adjusts per device
    // Create a temp element to measure the actual env() value
    const getSafeAreaTop = () => {
      const div = document.createElement('div');
      div.style.position = 'fixed';
      div.style.top = 'env(safe-area-inset-top, 0px)';
      div.style.visibility = 'hidden';
      document.body.appendChild(div);
      const top = parseInt(getComputedStyle(div).top, 10) || 0;
      document.body.removeChild(div);
      return top;
    };

    const safeAreaTop = getSafeAreaTop();

    // Calculate spotlight position based on mode
    let spotlightX, spotlightY, spotlightRadius;

    if (isBottomNavCardsSpotlight) {
      // Cards button is in the bottom nav - find it dynamically
      const cardsBtn = document.querySelector('[data-tutorial="nav-cards"]');
      if (cardsBtn) {
        const rect = cardsBtn.getBoundingClientRect();
        spotlightX = rect.left + rect.width / 2;
        spotlightY = rect.top + rect.height / 2;
        spotlightRadius = 36; // Slightly larger for the nav button
      } else {
        // Fallback position
        spotlightX = windowSize.width / 2 - 60;
        spotlightY = windowSize.height - 52;
        spotlightRadius = 36;
      }
    } else if (isBottomNavAddSpotlight) {
      // Add button (center pill) in the bottom nav
      const addBtn = document.querySelector('[data-tutorial="nav-add"]');
      if (addBtn) {
        const rect = addBtn.getBoundingClientRect();
        spotlightX = rect.left + rect.width / 2;
        spotlightY = rect.top + rect.height / 2;
        spotlightRadius = 40; // Larger for the pill button
      } else {
        // Fallback position (center of screen, near bottom)
        spotlightX = windowSize.width / 2;
        spotlightY = windowSize.height - 52;
        spotlightRadius = 40;
      }
    } else if (isBottomNavChatSpotlight) {
      // Chat button in the bottom nav
      const chatBtn = document.querySelector('[data-tutorial="nav-chat"]');
      if (chatBtn) {
        const rect = chatBtn.getBoundingClientRect();
        spotlightX = rect.left + rect.width / 2;
        spotlightY = rect.top + rect.height / 2;
        spotlightRadius = 36;
      } else {
        // Fallback position
        spotlightX = windowSize.width / 2 + 100;
        spotlightY = windowSize.height - 52;
        spotlightRadius = 36;
      }
    } else {
      // X position: from right edge - 16px padding - 18px (half button width)
      spotlightX = windowSize.width - 16 - 18;

      // Y position: safe area + header padding (12px) + half button height (18px)
      // Adding extra offset to move spotlight DOWN (lower on screen)
      spotlightY = safeAreaTop + 12 + 18 + 4; // +4px offset to lower it

      spotlightRadius = 24; // Slightly smaller than button for tighter fit
    }

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            ...styleSheet.spotlightOverlay,
            pointerEvents: 'auto', // Always block clicks on overlay
          }}
        >
          {/* Dark overlay with transparent hole and gradient */}
          <svg style={styleSheet.spotlightSvg} width={windowSize.width} height={windowSize.height}>
            <defs>
              <mask id="spotlight-mask">
                <rect width="100%" height="100%" fill="white" />
                <circle cx={spotlightX} cy={spotlightY} r={spotlightRadius} fill="black" />
              </mask>
              <linearGradient id="overlay-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="rgba(0, 0, 0, 0.4)" />
                <stop offset="60%" stopColor="rgba(0, 0, 0, 0.7)" />
                <stop offset="100%" stopColor="rgba(0, 0, 0, 0.85)" />
              </linearGradient>
            </defs>
            <rect
              width="100%"
              height="100%"
              fill="url(#overlay-gradient)"
              mask="url(#spotlight-mask)"
            />
          </svg>

          {/* Clickable pass-through hole for profile spotlight only */}
          {isProfileSpotlight && (
            <div
              style={{
                position: 'absolute',
                top: spotlightY - spotlightRadius,
                left: spotlightX - spotlightRadius,
                width: spotlightRadius * 2,
                height: spotlightRadius * 2,
                borderRadius: '50%',
                background: 'transparent',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                // Find and click the profile button
                const profileBtn = document.querySelector('[data-tutorial="profile-btn"]');
                if (profileBtn) profileBtn.click();
              }}
            />
          )}

          {/* Clickable pass-through hole for bottom nav cards spotlight */}
          {isBottomNavCardsSpotlight && (
            <div
              style={{
                position: 'absolute',
                top: spotlightY - spotlightRadius,
                left: spotlightX - spotlightRadius,
                width: spotlightRadius * 2,
                height: spotlightRadius * 2,
                borderRadius: '50%',
                background: 'transparent',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                // Find and click the Cards nav button
                const cardsBtn = document.querySelector('[data-tutorial="nav-cards"]');
                if (cardsBtn) cardsBtn.click();
              }}
            />
          )}

          {/* Clickable pass-through hole for bottom nav add spotlight */}
          {isBottomNavAddSpotlight && (
            <div
              style={{
                position: 'absolute',
                top: spotlightY - spotlightRadius,
                left: spotlightX - spotlightRadius,
                width: spotlightRadius * 2,
                height: spotlightRadius * 2,
                borderRadius: '24px', // Pill shape
                background: 'transparent',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                // Find and click the Add nav button
                const addBtn = document.querySelector('[data-tutorial="nav-add"]');
                if (addBtn) addBtn.click();
              }}
            />
          )}

          {/* Clickable pass-through hole for bottom nav chat spotlight */}
          {isBottomNavChatSpotlight && (
            <div
              style={{
                position: 'absolute',
                top: spotlightY - spotlightRadius,
                left: spotlightX - spotlightRadius,
                width: spotlightRadius * 2,
                height: spotlightRadius * 2,
                borderRadius: '50%',
                background: 'transparent',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                // Find and click the Chat nav button
                const chatBtn = document.querySelector('[data-tutorial="nav-chat"]');
                if (chatBtn) chatBtn.click();
              }}
            />
          )}

          {/* Arrow pointing to spotlight (for profile/info-icon) */}
          {isTypingComplete && !isBottomNavCardsSpotlight && !isBottomNavAddSpotlight && !isBottomNavChatSpotlight && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              style={{
                position: 'absolute',
                top: spotlightY + spotlightRadius + 8,
                right: 16 + 18 - 20 + 8, // Center arrow under button, +8 to move left
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                animation: 'bounceUp 1s infinite',
              }}
            >
              <svg width="40" height="40" viewBox="0 0 40 40">
                <path
                  d="M10 30 L30 10 M30 10 L18 10 M30 10 L30 22"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
              <div style={styleSheet.spotlightHint}>
                {isProfileSpotlight ? 'Click Profile' : <><div>Tutorials</div><div>are here</div></>}
              </div>
            </motion.div>
          )}

          {/* Arrow pointing DOWN to bottom nav spotlight (Cards) */}
          {isTypingComplete && isBottomNavCardsSpotlight && (
            <div
              style={{
                position: 'absolute',
                top: spotlightY - spotlightRadius - 70,
                left: spotlightX,
                transform: 'translateX(-50%)',
              }}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  animation: 'bounceDown 1s infinite',
                }}
              >
                <div style={styleSheet.spotlightHint}>Click Cards</div>
                <svg width="40" height="40" viewBox="0 0 40 40">
                  <path
                    d="M20 10 L20 30 M20 30 L12 22 M20 30 L28 22"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </motion.div>
            </div>
          )}

          {/* Arrow pointing DOWN to bottom nav spotlight (Add) */}
          {isTypingComplete && isBottomNavAddSpotlight && (
            <div
              style={{
                position: 'absolute',
                top: spotlightY - spotlightRadius - 70,
                left: spotlightX,
                transform: 'translateX(-50%)',
              }}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  animation: 'bounceDown 1s infinite',
                }}
              >
                <div style={styleSheet.spotlightHint}>Click Add</div>
                <svg width="40" height="40" viewBox="0 0 40 40">
                  <path
                    d="M20 10 L20 30 M20 30 L12 22 M20 30 L28 22"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </motion.div>
            </div>
          )}

          {/* Arrow pointing DOWN to bottom nav spotlight (Chat) */}
          {isTypingComplete && isBottomNavChatSpotlight && (
            <div
              style={{
                position: 'absolute',
                top: spotlightY - spotlightRadius - 70,
                left: spotlightX,
                transform: 'translateX(-50%)',
              }}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  animation: 'bounceDown 1s infinite',
                }}
              >
                <div style={styleSheet.spotlightHint}>Click Chat</div>
                <svg width="40" height="40" viewBox="0 0 40 40">
                  <path
                    d="M20 10 L20 30 M20 30 L12 22 M20 30 L28 22"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </motion.div>
            </div>
          )}

          {/* Content container - positioned at bottom (or top for bottom nav) */}
          <div
            style={{
              ...styleSheet.spotlightContent,
              ...((isBottomNavCardsSpotlight || isBottomNavAddSpotlight || isBottomNavChatSpotlight) ? { bottom: 'auto', top: '100px' } : {}),
            }}
          >
            <motion.div
              style={styleSheet.characterContainer}
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: contentVisible ? 1 : 0,
                y: contentVisible ? 0 : 20
              }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              <img
                src={(isBottomNavCardsSpotlight || isBottomNavAddSpotlight || isBottomNavChatSpotlight) ? ChaseWaveImage : characterImage}
                alt={(isBottomNavCardsSpotlight || isBottomNavAddSpotlight || isBottomNavChatSpotlight) ? "Chase" : "Chef"}
                style={styleSheet.characterImage}
              />
            </motion.div>

            <motion.div
              style={styleSheet.textBox}
              onClick={handleTextBoxTap}
              initial={{ opacity: 0, y: 15 }}
              animate={{
                opacity: contentVisible ? 1 : 0,
                y: contentVisible ? 0 : 15
              }}
              transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
            >
              <div style={styleSheet.textContent}>
                {message.map((line, idx) => (
                  <div key={idx} style={styleSheet.textLine}>
                    {parseHighlightedText(displayedText.split('\n')[idx]) || '\u00A0'}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Done button area - always reserve space to prevent layout shift */}
            {isInfoIconSpotlight && (
              <div style={styleSheet.doneButtonArea}>
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{
                    opacity: isTypingComplete ? 1 : 0,
                    y: isTypingComplete ? 0 : 10
                  }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  style={{
                    ...styleSheet.doneButton,
                    pointerEvents: isTypingComplete ? 'auto' : 'none',
                  }}
                  onClick={handleYes}
                >
                  {yesText}
                </motion.button>
              </div>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Intro Chase mode - Chef on right, Chase fading in on left
  if (mode === 'intro-chase') {
    const handleNext = () => {
      playClickSound();
      onNext?.();
    };

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          style={styleSheet.overlay}
        >
          <div style={styleSheet.container}>
            {/* Dual character container - Chase left, Chef right */}
            <motion.div
              style={styleSheet.dualCharacterContainer}
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: contentVisible ? 1 : 0,
                y: contentVisible ? 0 : 20
              }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: contentVisible ? 1 : 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                style={styleSheet.characterLeft}
              >
                <img src={ChaseWaveImage} alt="Chase" style={styleSheet.characterImage} />
              </motion.div>
              <div style={styleSheet.characterRight}>
                <img src={ChefImage} alt="Chef" style={styleSheet.characterImage} />
              </div>
            </motion.div>

            {/* Text Box */}
            <motion.div
              style={styleSheet.textBox}
              onClick={handleTextBoxTap}
              initial={{ opacity: 0, y: 15 }}
              animate={{
                opacity: contentVisible ? 1 : 0,
                y: contentVisible ? 0 : 15
              }}
              transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
            >
              <div style={styleSheet.textContent}>
                {message.map((line, idx) => (
                  <div key={idx} style={styleSheet.textLine}>
                    {parseHighlightedText(displayedText.split('\n')[idx]) || '\u00A0'}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Next button */}
            <div style={styleSheet.buttonArea}>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{
                  opacity: buttonsVisible ? 1 : 0,
                  y: buttonsVisible ? 0 : 10
                }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                style={styleSheet.buttonContainer}
              >
                <button
                  style={{
                    ...styleSheet.yesButton,
                    pointerEvents: buttonsVisible ? 'auto' : 'none',
                  }}
                  onClick={handleNext}
                >
                  Next
                </button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Chase Hello mode - Only Chase, with 3 action buttons
  if (mode === 'chase-hello') {
    const handleOption = (handler) => () => {
      playClickSound();
      handler?.();
    };

    const handleClose = () => {
      playClickSound();
      onClose?.();
    };

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          style={styleSheet.overlay}
        >
          {/* Close button in corner */}
          {onClose && (
            <button style={styleSheet.closeButton} onClick={handleClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18" />
                <path d="M6 6l12 12" />
              </svg>
            </button>
          )}
          <div style={styleSheet.container}>
            {/* Just Chase */}
            <motion.div
              style={styleSheet.characterContainer}
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: contentVisible ? 1 : 0,
                y: contentVisible ? 0 : 20
              }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              <img src={ChaseThumbsUpImage} alt="Chase" style={styleSheet.characterImage} />
            </motion.div>

            {/* Text Box */}
            <motion.div
              style={styleSheet.textBox}
              onClick={handleTextBoxTap}
              initial={{ opacity: 0, y: 15 }}
              animate={{
                opacity: contentVisible ? 1 : 0,
                y: contentVisible ? 0 : 15
              }}
              transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
            >
              <div style={styleSheet.textContent}>
                {message.map((line, idx) => (
                  <div key={idx} style={styleSheet.textLine}>
                    {parseHighlightedText(displayedText.split('\n')[idx]) || '\u00A0'}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* 3 Action buttons */}
            <div style={styleSheet.buttonArea}>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{
                  opacity: buttonsVisible ? 1 : 0,
                  y: buttonsVisible ? 0 : 10
                }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                style={styleSheet.buttonContainer}
              >
                <button
                  style={{
                    ...styleSheet.optionButton,
                    pointerEvents: buttonsVisible ? 'auto' : 'none',
                  }}
                  onClick={handleOption(onOption1)}
                >
                  {option1Text}
                </button>
                <button
                  style={{
                    ...styleSheet.optionButton,
                    pointerEvents: buttonsVisible ? 'auto' : 'none',
                  }}
                  onClick={handleOption(onOption2)}
                >
                  {option2Text}
                </button>
                <button
                  style={{
                    ...styleSheet.optionButton,
                    pointerEvents: buttonsVisible ? 'auto' : 'none',
                  }}
                  onClick={handleOption(onOption3)}
                >
                  {option3Text}
                </button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Chase Congrats mode - Chase celebrating after adding first card/binder
  if (mode === 'chase-congrats') {
    const handleContinue = () => {
      playClickSound();
      onContinue?.();
    };

    const handleSecondary = () => {
      playClickSound();
      onSecondary?.();
    };

    const handleClose = () => {
      playClickSound();
      onClose?.();
    };

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          style={styleSheet.overlay}
        >
          {/* Close button in corner */}
          {onClose && (
            <button style={styleSheet.closeButton} onClick={handleClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18" />
                <path d="M6 6l12 12" />
              </svg>
            </button>
          )}
          <div style={styleSheet.container}>
            {/* Chase celebrating */}
            <motion.div
              style={styleSheet.characterContainer}
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: contentVisible ? 1 : 0,
                y: contentVisible ? 0 : 20
              }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              <img src={ChaseThumbsUpImage} alt="Chase" style={styleSheet.characterImage} />
            </motion.div>

            {/* Text Box */}
            <motion.div
              style={styleSheet.textBox}
              onClick={handleTextBoxTap}
              initial={{ opacity: 0, y: 15 }}
              animate={{
                opacity: contentVisible ? 1 : 0,
                y: contentVisible ? 0 : 15
              }}
              transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
            >
              <div style={styleSheet.textContent}>
                {message.map((line, idx) => (
                  <div key={idx} style={styleSheet.textLine}>
                    {parseHighlightedText(displayedText.split('\n')[idx]) || '\u00A0'}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Continue + More Tutorials buttons */}
            <div style={styleSheet.buttonArea}>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{
                  opacity: buttonsVisible ? 1 : 0,
                  y: buttonsVisible ? 0 : 10
                }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                style={styleSheet.buttonContainer}
              >
                <button
                  style={{
                    ...styleSheet.yesButton,
                    pointerEvents: buttonsVisible ? 'auto' : 'none',
                  }}
                  onClick={handleContinue}
                >
                  {continueText}
                </button>
                {onSecondary && (
                  <button
                    style={{
                      ...styleSheet.noButton,
                      pointerEvents: buttonsVisible ? 'auto' : 'none',
                    }}
                    onClick={handleSecondary}
                  >
                    {secondaryText}
                  </button>
                )}
              </motion.div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Normal mode
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        style={styleSheet.overlay}
      >
        <div style={styleSheet.container}>
          {/* Character - fades in with slight rise */}
          <motion.div
            style={styleSheet.characterContainer}
            initial={{ opacity: 0, y: 20 }}
            animate={{
              opacity: contentVisible ? 1 : 0,
              y: contentVisible ? 0 : 20
            }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <img
              src={characterImage}
              alt="Chef"
              style={styleSheet.characterImage}
            />
          </motion.div>

          {/* Text Box - fades in with slight rise, slightly delayed */}
          <motion.div
            style={styleSheet.textBox}
            onClick={handleTextBoxTap}
            initial={{ opacity: 0, y: 15 }}
            animate={{
              opacity: contentVisible ? 1 : 0,
              y: contentVisible ? 0 : 15
            }}
            transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
          >
            {/* Pre-render all lines to maintain fixed height */}
            <div style={styleSheet.textContent}>
              {message.map((line, idx) => (
                <div key={idx} style={styleSheet.textLine}>
                  {/* Show typed portion of this line */}
                  {parseHighlightedText(displayedText.split('\n')[idx]) || '\u00A0'}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Button area - always takes up space, buttons fade in */}
          {!hideButtons && (
            <div style={styleSheet.buttonArea}>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{
                  opacity: buttonsVisible ? 1 : 0,
                  y: buttonsVisible ? 0 : 10
                }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                style={styleSheet.buttonContainer}
              >
                <button
                  style={{
                    ...styleSheet.yesButton,
                    pointerEvents: buttonsVisible ? 'auto' : 'none',
                  }}
                  onClick={handleYes}
                >
                  {yesText}
                </button>
                {noText && (
                  <button
                    style={{
                      ...styleSheet.noButton,
                      pointerEvents: buttonsVisible ? 'auto' : 'none',
                    }}
                    onClick={handleNo}
                  >
                    {noText}
                  </button>
                )}
              </motion.div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

const styles = (theme, isDark) => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent', // Backdrop handles the dark background
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
    padding: '20px',
  },
  spotlightOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    pointerEvents: 'none',
  },
  spotlightSvg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
  },
  spotlightContent: {
    position: 'absolute',
    bottom: '120px',
    left: '20px',
    right: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    pointerEvents: 'auto',
  },
  spotlightArrow: {
    position: 'absolute',
    top: '95px',
    right: '14px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    animation: 'bounceUp 1s infinite',
  },
  spotlightHint: {
    fontFamily: "'PokeText', monospace",
    fontSize: '11px',
    color: 'white',
    textShadow: '0 2px 4px rgba(0,0,0,0.8)',
    whiteSpace: 'nowrap',
  },
  doneButtonArea: {
    width: '100%',
    maxWidth: '360px',
    marginTop: '16px',
  },
  doneButton: {
    width: '100%',
    padding: '14px 20px',
    backgroundColor: theme.accent,
    border: 'none',
    borderRadius: '10px',
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'PokeText', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '360px',
    width: '100%',
  },
  characterContainer: {
    marginBottom: '16px',
  },
  dualCharacterContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    gap: '20px',
    marginBottom: '16px',
    width: '100%',
  },
  characterLeft: {
    display: 'flex',
    justifyContent: 'center',
  },
  characterRight: {
    display: 'flex',
    justifyContent: 'center',
  },
  characterImage: {
    width: '120px',
    height: 'auto',
    filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.5))',
  },
  textBox: {
    background: 'white',
    border: '3px solid #374151',
    borderRadius: '8px',
    padding: '14px 12px',
    width: '100%',
    minHeight: '130px', // Fixed height for 5 lines
    position: 'relative',
    cursor: 'pointer',
  },
  textContent: {
    fontFamily: "'PokeText', monospace",
    fontSize: '14px',
    lineHeight: '1.5',
    color: '#1F2937',
    width: '100%',
  },
  textLine: {
    height: '21px', // Fixed line height
    width: '100%',
    whiteSpace: 'nowrap',
  },
  cursor: {
    position: 'absolute',
    fontFamily: "'PokeText', monospace",
    fontSize: '13px',
    color: '#1F2937',
    animation: 'blink 0.8s infinite',
  },
  tapHint: {
    position: 'absolute',
    bottom: '6px',
    right: '10px',
    fontSize: '9px',
    color: '#9CA3AF',
    fontFamily: "'PokeText', monospace",
  },
  buttonArea: {
    width: '100%',
    minHeight: '116px', // Reserve space for buttons so no layout shift
    marginTop: '16px',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%',
  },
  yesButton: {
    width: '100%',
    padding: '14px 20px',
    backgroundColor: theme.accent,
    border: 'none',
    borderRadius: '10px',
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'PokeText', -apple-system, BlinkMacSystemFont, sans-serif",
    transition: 'transform 0.1s, background-color 0.2s',
  },
  noButton: {
    width: '100%',
    padding: '14px 20px',
    backgroundColor: 'transparent',
    border: `2px solid ${theme.text}`,
    borderRadius: '10px',
    color: theme.text,
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: "'PokeText', -apple-system, BlinkMacSystemFont, sans-serif",
    transition: 'transform 0.1s, border-color 0.2s',
  },
  optionButton: {
    width: '100%',
    padding: '14px 20px',
    backgroundColor: theme.bgCard,
    border: `2px solid ${isDark ? '#4a4a4a' : theme.border}`,
    borderRadius: '10px',
    color: theme.text,
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: "'PokeText', -apple-system, BlinkMacSystemFont, sans-serif",
    transition: 'transform 0.1s, border-color 0.2s',
  },
  arrowContainerTopRight: {
    position: 'fixed',
    top: '70px',
    right: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  arrowUpRight: {
    animation: 'bounceUp 1s infinite',
  },
  tapProfileHint: {
    fontFamily: "'PokeText', monospace",
    fontSize: '12px',
    color: 'white',
    textShadow: '0 2px 4px rgba(0,0,0,0.5)',
  },
  closeButton: {
    position: 'absolute',
    top: 'calc(env(safe-area-inset-top, 0px) + 16px)',
    right: '16px',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 10001,
  },
});

// Add keyframes for cursor blink and arrow bounce
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }
  @keyframes bounceUp {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }
  @keyframes bounceDown {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(10px); }
  }
`;
document.head.appendChild(styleSheet);

export default TutorialOverlay;
